import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableItemProps {
  id: string;
  children: (dragHandleProps: Record<string, unknown>) => React.ReactNode;
}

function SortableItem({ id, children }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      data-row-id={id}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
    >
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

interface TaskListDndProps<T extends { id: string }> {
  /** Unique id for this list's droppable container — `'toplevel'` for a project's top-level
   * tasks, `` `task:${taskId}` `` for a task's subtasks, or `` `bucket:${key}` `` for a dashboard
   * bucket. Used by the page-level DndContext to resolve which list a drag ended over, including
   * cross-container moves. */
  containerId: string;
  /** The visible/rendered items, in their current display order. May be a filtered subset of the
   * full sibling list — the caller (not this component) is responsible for merging the reordered
   * subset back with any hidden siblings and persisting the result, since only the caller knows
   * about items this list isn't rendering. */
  items: T[];
  renderItem: (item: T, dragHandleProps: Record<string, unknown>) => React.ReactNode;
}

/** Sortable region reused across every drag-reorderable list in the admin UI (a project's
 * top-level tasks, a task's subtasks, and a dashboard bucket). Does not own a DndContext itself —
 * the page owns one shared DndContext spanning every visible list so items can be dragged across
 * containers, not just reordered within one. Generic over any row type with an `id`, so it works
 * for both `TaskItem` (ProjectDetailPage) and `DashboardTaskItem` (DashboardPage). */
export function TaskListDnd<T extends { id: string }>({ containerId, items, renderItem }: TaskListDndProps<T>) {
  const { setNodeRef } = useDroppable({ id: containerId });

  return (
    <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
      <div ref={setNodeRef} className={`space-y-2 ${items.length === 0 ? 'min-h-[2.5rem]' : ''}`}>
        {items.map((item) => (
          <SortableItem key={item.id} id={item.id}>
            {(dragHandleProps) => renderItem(item, dragHandleProps)}
          </SortableItem>
        ))}
      </div>
    </SortableContext>
  );
}
